import { useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  attachToMessage,
  attachToTicket,
  getTicket,
  replyToTicket,
  type PAAttachment,
  type PATicketDetail,
  type PATicketMessage,
} from '../api/tickets';
import { downloadFile } from '../api/download';
import { formatBytesUnit, formatInstant } from '../format';
import { FactList } from '../money/DataTable';
import { QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { ticketAuthorLabel, ticketOriginLabel } from '../money/labels';
import { errorText } from '../session/errorText';
import { routePath, type Navigation } from '../router';

/**
 * ============================================================================
 * ONE TICKET — the thread, its files and the reply form (§26b, F26)
 * ============================================================================
 *
 * ⚠ IT IS ITS OWN URL (`/tickets/{id}`), which is the one parametrised path in this app —
 * see `router.ts`'s `Route` type. The thread is a page a subscriber lives in: he reads it,
 * replies, attaches a photograph of an error, and comes back to it after a message arrives.
 * Held in component state instead, the BACK GESTURE would leave the whole section, a reload
 * would lose it, and the address could not be read out to support.
 *
 * # The five rules
 *
 *  1. **THE REPLY FORM IS OFFERED OFF `status.allow_reply` AND NOTHING ELSE.** It is the
 *     server's own verdict and Appendix M §2.1 calls it the ONLY thing that ever stops a
 *     subscriber writing — a «Выполнен» status does NOT stop him. So `is_done` is not the
 *     predicate, and a form offered off it would fail on Send with
 *     `409 pa.ticket_reply_not_allowed` for a ticket that was perfectly answerable, or hide
 *     itself for one that was.
 *  2. **THE AUTHOR IS A KIND, NEVER A NAME.** There is no name on this wire to render:
 *     §26b's own screenshot shows «Администратор», a label rather than a person, and staff
 *     PII does not belong on an internet-facing listener. «Вы» / «Оператор» / «Система» are
 *     the portal's three words.
 *  3. **NO UPLOAD CONTROL WHEN `attachments_available` IS FALSE.** On an installation with
 *     no blob storage every attach answers `503 pa.attachments_unavailable` (~~`pa.storage_
 *     unavailable`~~ until 2026-08-12: that is the DOCUMENT surface's code, and its message
 *     tells a subscriber attaching a photograph that a document has been produced), so the
 *     picker would be a control whose only outcome is an error. The ALREADY-ATTACHED rows are
 *     still rendered in that state, deliberately: they are a truthful record of what was sent, and
 *     hiding them because a blob root moved would make a lost file look like a file nobody
 *     ever sent.
 *  4. **THE CEILING IS THE SERVER'S FIGURE** (`attachment_max_bytes`), stated beside the
 *     picker and enforced before the upload — so a 40 MB video is refused without spending
 *     the subscriber's uplink on it, and the number he reads is the installation's own rather
 *     than a constant compiled into this app.
 *  5. **`download_url` IS FOLLOWED, NEVER BUILT.** Composing a file path from ids would be a
 *     client deciding which ids are reachable, which is what the flat `404 pa.not_found`
 *     exists to prevent. `TicketScreen.test.tsx` sweeps the sources for a composed path.
 *
 * # Why a reply and its file are TWO requests, and what happens when the second fails
 *
 * The wire attaches a file to a MESSAGE — which is what §26b's form draws and what the
 * administrative thread does, so a reply and its screenshot stay together — and a message id
 * exists only once the reply has been posted. So the order is fixed: reply, then attach. If
 * the attach then fails, **the reply is already in the operator's queue** and this screen
 * says so in as many words rather than reporting a failure that would make him type it again.
 * A file on the TICKET (the «Прикрепить файл» control above the thread) is ONE request and
 * has no such seam.
 */
export function TicketScreen({
  id,
  navigate,
}: {
  id: number;
  navigate: Navigation['navigate'];
}) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PATicketDetail>(() => getTicket(id), [id]);

  return (
    <>
      {/* A real link back to the list: the strip highlights «Поддержка» while this is open,
          but the way OUT of a sub-page must be on the sub-page. */}
      <p className="pa-page-lead">
        <a
          href={routePath({ kind: 'tickets' })}
          onClick={(event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            navigate({ kind: 'tickets' });
          }}
        >
          {t('ticket.back')}
        </a>
      </p>
      <QueryFrame query={query} reload={reload}>
        {(detail) => <TicketBody detail={detail} reload={reload} />}
      </QueryFrame>
    </>
  );
}

function TicketBody({ detail, reload }: { detail: PATicketDetail; reload: () => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');

  const fail = (err: unknown) => {
    setNotice('');
    setError(err);
  };
  const succeed = (message: string) => {
    setError(null);
    setNotice(message);
  };

  /** Attachments hanging off the TICKET itself — `message_id === null`. */
  const own = detail.attachments.filter((a) => a.message_id === null);

  return (
    <>
      {notice ? (
        <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
      ) : null}
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
      ) : null}

      <Card
        className="pa-block"
        title={detail.subject}
        extra={
          <span
            className="pa-status-badge"
            style={detail.status.color ? { color: detail.status.color } : undefined}
          >
            {detail.status.name}
          </span>
        }
      >
        <FactList facts={ticketFacts(detail, t)} />

        <div className="pa-subblock">
          <h3 className="pa-section-subtitle">{t('ticket.filesTitle')}</h3>
          <AttachmentList files={own} onFailure={fail} emptyKey="ticket.noFiles" />
          {/* RULE 3: no picker at all when the installation cannot store bytes. */}
          {detail.attachments_available ? (
            <FilePicker
              idSuffix="ticket"
              maxBytes={detail.attachment_max_bytes}
              busyLabelKey="ticket.attaching"
              labelKey="ticket.attach"
              onUpload={(file) => attachToTicket(detail.id, file)}
              onDone={(attachment) => {
                succeed(t('ticket.attached', { name: attachment.filename }));
                reload();
              }}
              onFailure={fail}
            />
          ) : null}
        </div>
      </Card>

      <Card className="pa-block" title={t('ticket.threadTitle')}>
        {detail.messages.length === 0 ? (
          <Alert type="info" showIcon message={t('ticket.threadEmpty')} />
        ) : (
          <div className="pa-thread">
            {detail.messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                files={detail.attachments.filter((a) => a.message_id === message.id)}
                onFailure={fail}
              />
            ))}
          </div>
        )}
      </Card>

      {/* RULE 1. */}
      {detail.status.allow_reply ? (
        <ReplyForm detail={detail} onDone={succeed} onFailure={fail} reload={reload} />
      ) : (
        <Alert type="info" showIcon message={t('ticket.replyClosed')} />
      )}
    </>
  );
}

/** The header facts. `origin` is here rather than in the title: it is a fact, not a label. */
function ticketFacts(
  detail: PATicketDetail,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode }> {
  const facts = [
    { key: 'id', label: t('ticket.idLabel'), value: <span className="mono">{detail.id}</span> },
    {
      key: 'class',
      label: t('ticket.classLabel'),
      value: detail.class?.name ?? '—',
    },
    { key: 'origin', label: t('ticket.originLabel'), value: ticketOriginLabel(detail.origin, t) },
    { key: 'created', label: t('ticket.createdLabel'), value: formatInstant(detail.created_at) },
    { key: 'updated', label: t('ticket.updatedLabel'), value: formatInstant(detail.updated_at) },
  ];
  return facts;
}

/** One thread entry: who, when, the text as TEXT, and whatever hangs off it. */
function Message({
  message,
  files,
  onFailure,
}: {
  message: PATicketMessage;
  files: readonly PAAttachment[];
  onFailure: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const mine = message.author_kind === 'subscriber';

  return (
    <div className={`pa-msg ${mine ? 'pa-msg-mine' : ''}`} data-testid={`pa-msg-${message.id}`}>
      <p className="pa-msg-head">
        <span className="pa-msg-author">{ticketAuthorLabel(message.author_kind, t)}</span>
        <span className="pa-msg-when">{formatInstant(message.created_at)}</span>
        {/* An edit is SAID. Staff may correct a message after posting, and a thread that
            changed under him with no trace is a thread he cannot trust. */}
        {message.edited_at ? (
          <span className="pa-msg-edited">
            {t('ticket.edited', { when: formatInstant(message.edited_at) })}
          </span>
        ) : null}
      </p>
      {/*
        ⚠ THE OPERATOR'S TEXT, AS TEXT. React escapes it; the CSS preserves the newlines.
        There is no format column behind this string and no sanitizer between here and a
        subscriber's session, so `dangerouslySetInnerHTML` would make every staff reply an
        injection surface. `TicketScreen.test.tsx` sweeps for it and renders a `<script>`.
      */}
      <p className="pa-msg-body">{message.body}</p>
      {files.length > 0 ? <AttachmentList files={files} onFailure={onFailure} /> : null}
    </div>
  );
}

/**
 * The attachment rows. Each is a REAL anchor on the server's own `download_url`, with the
 * click intercepted so that a refusal becomes a sentence.
 *
 * ⚠ `410 pa.attachment_gone` IS THE REASON THE CLICK IS INTERCEPTED AT ALL. The row exists
 * and its bytes do not — a truthful record of something that WAS stored — and the server
 * distinguishes it from a wrong link on purpose. A plain anchor would hand that to the
 * browser, which says «Failed».
 */
function AttachmentList({
  files,
  onFailure,
  emptyKey,
}: {
  files: readonly PAAttachment[];
  onFailure: (err: unknown) => void;
  emptyKey?: string;
}) {
  const { t } = useTranslation();
  if (files.length === 0) {
    return emptyKey ? <p className="pa-field-hint">{t(emptyKey)}</p> : null;
  }
  return (
    <ul className="pa-attachments">
      {files.map((file) => (
        <li className="pa-attach" key={file.id}>
          <AttachmentLink file={file} onFailure={onFailure} />
          <span className="pa-attach-size">{sizeText(file.size_bytes, t)}</span>
        </li>
      ))}
    </ul>
  );
}

function AttachmentLink({
  file,
  onFailure,
}: {
  file: PAAttachment;
  onFailure: (err: unknown) => void;
}) {
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();
  return (
    <a
      className="pa-download"
      // RULE 5: the SERVER's url, passed through. Nothing here composes one.
      href={file.download_url}
      download
      onClick={(e) => {
        // A modified click is the browser's — see `DocumentsScreen`'s own note.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        onFailure(null);
        setBusy(true);
        void downloadFile(file.download_url, file.filename)
          .catch(onFailure)
          .finally(() => setBusy(false));
      }}
    >
      {busy ? t('ticket.downloading') : file.filename}
    </a>
  );
}

/** sizeText renders a file size as «N КБ» / «N МБ» — the unit word is the catalogue's. */
function sizeText(bytes: number, t: TFunction): string {
  const { value, unitKey } = formatBytesUnit(bytes);
  return `${value} ${t(unitKey)}`;
}

/**
 * The picker: a native file input, the server's ceiling beside it, and a local refusal above
 * that ceiling.
 *
 * A native `<input type="file">` rather than AntD's `Upload`, because `Upload` owns the
 * request and this app's uploads must go through `api.upload` — the one path where a refusal
 * arrives as the `ApiError` `errorText` renders. It is also what makes the ceiling
 * enforceable before a byte leaves the handset.
 */
function FilePicker({
  idSuffix,
  maxBytes,
  labelKey,
  busyLabelKey,
  onUpload,
  onDone,
  onFailure,
}: {
  idSuffix: string;
  maxBytes: number;
  labelKey: string;
  busyLabelKey: string;
  onUpload: (file: File) => Promise<PAAttachment>;
  onDone: (attachment: PAAttachment) => void;
  onFailure: (err: unknown) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const inputID = `pa-ticket-file-${idSuffix}`;
  const ref = useRef<HTMLInputElement>(null);

  const send = async (file: File) => {
    // RULE 4: the ceiling is the server's, and it is enforced here so that a photograph too
    // large is refused in the room rather than after a minute of uplink.
    if (maxBytes > 0 && file.size > maxBytes) {
      const { value, unitKey } = formatBytesUnit(maxBytes);
      setLocalError(t('ticket.tooLarge', { limit: `${value} ${t(unitKey)}` }));
      return;
    }
    setLocalError('');
    setBusy(true);
    try {
      onDone(await onUpload(file));
      if (ref.current) ref.current.value = '';
    } catch (err) {
      onFailure(err);
    } finally {
      setBusy(false);
    }
  };

  const { value: limitValue, unitKey: limitUnit } = formatBytesUnit(maxBytes);

  return (
    <div className="pa-inline-form">
      <label className="pa-field-label" htmlFor={inputID}>
        {t(labelKey)}
      </label>
      <input
        id={inputID}
        ref={ref}
        className="pa-file"
        type="file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void send(file);
        }}
      />
      {maxBytes > 0 ? (
        <p className="pa-field-hint">
          {t('ticket.maxSize', { limit: `${limitValue} ${t(limitUnit)}` })}
        </p>
      ) : null}
      {busy ? <p className="pa-field-hint">{t(busyLabelKey)}</p> : null}
      {localError ? <p className="pa-field-error">{localError}</p> : null}
    </div>
  );
}

/** §26b's «Новое сообщение»: the text, an optional file, and the two-request order. */
function ReplyForm({
  detail,
  onDone,
  onFailure,
  reload,
}: {
  detail: PATicketDetail;
  onDone: (message: string) => void;
  onFailure: (err: unknown) => void;
  reload: () => void;
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const ref = useRef<HTMLInputElement>(null);

  const send = async () => {
    const text = body.trim();
    if (text === '') {
      setLocalError(t('ticket.bodyRequired'));
      return;
    }
    if (file && detail.attachment_max_bytes > 0 && file.size > detail.attachment_max_bytes) {
      const { value, unitKey } = formatBytesUnit(detail.attachment_max_bytes);
      setLocalError(t('ticket.tooLarge', { limit: `${value} ${t(unitKey)}` }));
      return;
    }
    setBusy(true);
    setLocalError('');
    let posted;
    try {
      posted = await replyToTicket(detail.id, text);
    } catch (err) {
      setBusy(false);
      onFailure(err);
      return;
    }
    // ⚠ THE REPLY IS IN. From here on a failure must not read as «your message was not
    // sent», because it was — see this file's header.
    setBody('');
    if (!file) {
      setBusy(false);
      onDone(t('ticket.replied'));
      reload();
      return;
    }
    try {
      await attachToMessage(detail.id, posted.id, file);
      onDone(t('ticket.repliedWithFile'));
    } catch (err) {
      onFailure(err);
      // The sentence the seam needs: the message arrived and the file did not.
      setLocalError(t('ticket.replySentFileFailed'));
    } finally {
      setFile(null);
      if (ref.current) ref.current.value = '';
      setBusy(false);
      reload();
    }
  };

  const { value: limitValue, unitKey: limitUnit } = formatBytesUnit(detail.attachment_max_bytes);

  return (
    <Card className="pa-block" title={t('ticket.replyTitle')}>
      <div className="pa-inline-form">
        <label className="pa-field-label" htmlFor="pa-ticket-reply">
          {t('ticket.replyLabel')}
        </label>
        <Input.TextArea
          id="pa-ticket-reply"
          rows={5}
          value={body}
          placeholder={t('ticket.replyPlaceholder')}
          onChange={(e) => {
            setLocalError('');
            setBody(e.target.value);
          }}
        />
      </div>

      {/* RULE 3 again, on the other door: no picker here either when storage is absent. */}
      {detail.attachments_available ? (
        <div className="pa-inline-form">
          <label className="pa-field-label" htmlFor="pa-ticket-reply-file">
            {t('ticket.replyFile')}
          </label>
          <input
            id="pa-ticket-reply-file"
            ref={ref}
            className="pa-file"
            type="file"
            onChange={(e) => {
              setLocalError('');
              setFile(e.target.files?.[0] ?? null);
            }}
          />
          {detail.attachment_max_bytes > 0 ? (
            <p className="pa-field-hint">
              {t('ticket.maxSize', { limit: `${limitValue} ${t(limitUnit)}` })}
            </p>
          ) : null}
        </div>
      ) : null}

      {localError ? <p className="pa-field-error">{localError}</p> : null}

      <div className="pa-actions">
        <Button type="primary" size="large" block loading={busy} onClick={() => void send()}>
          {t('ticket.send')}
        </Button>
      </div>
    </Card>
  );
}
